if (!customElements.get('cws-wholesale-account-modal')) {
  class CwsWholesaleAccountModal extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('[data-cws-wholesale-form]');
      this.closeButton = this.querySelector('[data-cws-wholesale-close]');
      this.submitLink = this.querySelector('[data-cws-wholesale-submit]');
      this.triggerUrl = this.dataset.triggerUrl;
      this.fallbackUrl = this.dataset.fallbackUrl;
      this.lastFocused = null;
      this.hideTimer = null;
      this.previousBodyOverflow = '';
      this.isPageLocked = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleCloseClick = this.close.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleSubmitLink = this.handleSubmitLink.bind(this);

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('click', this.handleBackdropClick);
      this.closeButton?.addEventListener('click', this.handleCloseClick);
      this.form?.addEventListener('submit', this.handleSubmit);
      this.submitLink?.addEventListener('click', this.handleSubmitLink);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleBackdropClick);
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.submitLink?.removeEventListener('click', this.handleSubmitLink);
      clearTimeout(this.hideTimer);
      this.unlockPage();
    }

    handleDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest('a[href]');
      if (!link || this.contains(link) || link.target === '_blank' || !this.matchesTrigger(link.href)) return;

      event.preventDefault();
      this.open(link);
    }

    matchesTrigger(href) {
      if (!href || !this.triggerUrl) return false;

      try {
        const candidate = new URL(href, document.baseURI);
        const trigger = new URL(this.triggerUrl, document.baseURI);
        return candidate.pathname === trigger.pathname;
      } catch (error) {
        return false;
      }
    }

    open(trigger) {
      clearTimeout(this.hideTimer);
      this.lastFocused = trigger || document.activeElement;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.lockPage();

      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const firstField = this.querySelector('input:not([type="checkbox"]), select');
        firstField?.focus({ preventScroll: true });
      });
    }

    close() {
      if (this.hidden) return;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.unlockPage();

      const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
      this.hideTimer = window.setTimeout(() => {
        this.hidden = true;
        this.lastFocused?.focus?.({ preventScroll: true });
      }, delay);
    }

    lockPage() {
      if (this.isPageLocked) return;

      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.isPageLocked = true;
    }

    unlockPage() {
      if (!this.isPageLocked) return;

      document.body.style.overflow = this.previousBodyOverflow;
      this.isPageLocked = false;
    }

    handleBackdropClick(event) {
      if (event.target === this) this.close();
    }

    handleKeydown(event) {
      if (this.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])')
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.form?.reportValidity()) this.continueToApplication();
    }

    handleSubmitLink(event) {
      if (!this.form?.reportValidity()) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      this.continueToApplication();
    }

    continueToApplication() {
      if (this.fallbackUrl) window.location.assign(this.fallbackUrl);
    }
  }

  customElements.define('cws-wholesale-account-modal', CwsWholesaleAccountModal);
}
